// «Mensagem no rodapé do cupom» (recurso do Profissional; Pedro, 23/10/2026, pedidos da Pajú Burguer e da Mega
// Lanches): o texto da loja TROCA o «Obrigado pela preferência!», nos 4 modos. A regra do que pode ser gravado
// mora aqui porque vale no painel (o aviso enquanto a loja digita) e no servidor (400): só passa o que sai igual nas
// três impressões térmicas, para a loja não descobrir o «?» no papel.
import { wrapWords } from "./thermal-printer";

export const RODAPE_MAX_CARACTERES = 200;
/** O que sai sem mensagem. Igual a ele (depois de normalizar) é o mesmo que não ter mensagem: grava null. */
export const RODAPE_PADRAO = "Obrigado pela preferência!";
export const RODAPE_MAX_LINHAS = 6;

// Pontuação que o celular e o Word trocam sozinhos: vira a equivalente ASCII, como o modo Imagem já fazia. Os
// invisíveis (os que acompanham o emoji, o espaço de largura zero) saem: a loja não teria o que apagar.
const PONTUACAO: Array<[RegExp, string]> = [
  [/[‘’‚]/g, "'"],
  [/[“”„]/g, '"'],
  [/[–—―•]/g, "-"],
  [/…/g, "..."],
  [/[\u00A0\u202F]/g, " "],
  [/[\u200B-\u200D\u2060\uFEFF\uFE0E\uFE0F]/g, ""],
];

/** O texto como é gravado: pontuação trocada, espaços simples, sem linha vazia nas pontas nem duas seguidas. */
export function normalizarRodape(texto: string | null | undefined): string {
  let t = String(texto ?? "").normalize("NFC").replace(/\r\n?/g, "\n");
  for (const [re, troca] of PONTUACAO) t = t.replace(re, troca);
  const linhas: string[] = [];
  for (const l of t.split("\n").map((x) => x.replace(/[ \t]+/g, " ").trim())) {
    if (l === "" && (linhas.length === 0 || linhas[linhas.length - 1] === "")) continue;
    linhas.push(l);
  }
  while (linhas.length > 0 && linhas[linhas.length - 1] === "") linhas.pop();
  return linhas.join("\n");
}

// Sai igual no Texto (CP850), na Imagem e no Simples: ASCII, as letras do Latin-1 que no Simples só perdem o acento
// e º ª ° ½ (no Simples, o, a, o e 1/2). Æ, ß, ×, © e companhia passam no CP850 mas viram «?» no Simples.
const SIMBOLOS = new Set(["º", "ª", "°", "½"]);
function saiNoCupom(ch: string): boolean {
  const cp = ch.codePointAt(0)!;
  if (ch === "\n" || (cp >= 0x20 && cp <= 0x7e) || SIMBOLOS.has(ch)) return true;
  return cp >= 0xc0 && cp <= 0xff && /^[A-Za-z]$/.test(ch.normalize("NFD").replace(/[\u0300-\u036F]/g, ""));
}

// Emoji com cor ou com pessoas é mais de um código: no aviso, aparece como a loja vê.
function grafemas(t: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: new (l: string, o: { granularity: "grapheme" }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  return Segmenter ? Array.from(new Segmenter("pt-BR", { granularity: "grapheme" }).segment(t), (s) => s.segment) : Array.from(t);
}

/** O que se grava: o texto normalizado, ou null quando é vazio ou o próprio Obrigado (o cupom de sempre). */
export function rodapeParaGravar(texto: string | null | undefined): string | null {
  const t = normalizarRodape(texto);
  return !t || t === RODAPE_PADRAO ? null : t;
}

/** Os caracteres que não saem no cupom, sem repetir e na ordem em que aparecem. */
export function caracteresForaDoRodape(texto: string): string[] {
  const fora: string[] = [];
  for (const g of grafemas(normalizarRodape(texto))) {
    if (!Array.from(g).every(saiNoCupom) && !fora.includes(g)) fora.push(g);
  }
  return fora;
}

/** O aviso do painel e o 400 do servidor; null = pode gravar. */
export function erroDoRodape(texto: string): string | null {
  const t = normalizarRodape(texto);
  const fora = caracteresForaDoRodape(t);
  if (fora.length > 0) return `Não sai na impressora: ${fora.join(" ")}. Tire ${fora.length === 1 ? "esse caractere" : "esses caracteres"}.`;
  if (Array.from(t).length > RODAPE_MAX_CARACTERES) return `A mensagem do rodapé vai até ${RODAPE_MAX_CARACTERES} caracteres.`;
  if (t.split("\n").length > RODAPE_MAX_LINHAS) return `A mensagem do rodapé vai até ${RODAPE_MAX_LINHAS} linhas.`;
  return null;
}

/** As linhas no papel: as quebras que a loja digitou e o resto na largura, sem cortar palavra (só a maior que a linha). */
export function linhasDoRodape(texto: string, cols: number): string[] {
  return texto.split("\n").flatMap((p) => (p === "" ? [""] : wrapWords(p, cols)));
}
